/*
 * Cart progress bar (Corner-style card + pill track) with two independently
 * configurable milestones -- free shipping + a free "mystery" gift -- plus
 * auto-add/remove of the gift line item.
 *
 * GIFT <-> FREE SHIPPING COUPLING (order #15853 fix)
 * --------------------------------------------------
 * The mystery gift must never ride along on an order that does NOT qualify
 * for free shipping. Order #15853 had a $91.02 product subtotal (below the
 * $100 bar, so shipping was correctly charged) yet still carried the free
 * gift -- the shopper had >= $100, the gift auto-added, they dropped an item
 * back under $100, and checked out before the async auto-remove finished.
 *
 * Two defenses now prevent that:
 *   1. giftQualifies() couples the gift to the FREE-SHIPPING threshold: the
 *      gift is only kept when qualifyingSubtotal clears BOTH the gift
 *      threshold AND the shipping threshold. Under the free-shipping bar
 *      there is simply no gift.
 *   2. guardGiftCheckout() intercepts the Checkout button: if an unqualified
 *      gift is still in the cart at click time, it strips the gift first and
 *      then proceeds. It FAILS OPEN -- if the removal hangs, checkout still
 *      goes through, so a sale is never blocked. (Express/Buy-Now buttons
 *      bypass this; the server-side order flag is the backstop for those.)
 *
 * "Qualifying subtotal" = cart.total_price MINUS the price of any line that
 * carries the _mystery_gift=true property (see cart-progress-gift-bar.liquid),
 * so the gift itself never counts toward unlocking itself.
 *
 * The gift milestone (data-gift-available) only comes through as true when the
 * gift product is enabled AND actually in stock (see the Liquid file) -- if it
 * goes out of stock while already sitting in someone's cart as a line item,
 * syncGift() below removes it, exactly like it would if the cart dropped below
 * the dollar threshold.
 *
 * Currency: window.Shopify.currency is set once, in the page's initial HTML,
 * and does NOT update itself if the shopper switches currency through an
 * AJAX-based localization control without a full page reload -- only the
 * server-rendered Liquid content (money formats, cart totals) is guaranteed
 * fresh on every request/section-refresh. So data-currency-code (from
 * cart.currency.iso_code, always fresh) is treated as the source of truth
 * for the currency LABEL, and Shopify.currency.rate is only trusted for the
 * conversion MATH when its .active code agrees with data-currency-code --
 * otherwise conversionRate falls back to 1 rather than applying a rate for
 * the wrong currency.
 *
 * Gift add/remove and section refreshes go through the shared QUEUED cart
 * engine (window.arCartMutate / window.arCartRefreshSections in cart.js), so
 * they are serialized with any other in-flight cart action -- colliding
 * requests were the source of intermittent "There was an error while
 * updating your cart" messages.
 *
 * External-change watcher: discount codes on this store can be applied by
 * apps (e.g. "Discounty") or Shopify's own cart-level discount UI, which
 * don't run through this theme's cart engine -- so nothing tells the cart to
 * re-check itself afterwards. watchExternalCartChanges() below watches the
 * rendered total-price text for changes and triggers a resync when it moves.
 * It deliberately IGNORES changes made by our own engine (which sets
 * window.__arCartQuietUntil while it updates the DOM) so normal cart actions
 * don't trigger redundant extra requests.
 */
class CartProgressBar extends HTMLElement {
  constructor() {
    super();

    const serverCurrency = this.dataset.currencyCode || '';
    const clientCurrency = (window.Shopify && window.Shopify.currency) || {};
    const clientCurrencyMatches = !clientCurrency.active || !serverCurrency || clientCurrency.active === serverCurrency;

    // Always trust the server-rendered currency code for the label. Only
    // trust the client rate for the conversion math when the client's own
    // currency code agrees with it -- otherwise Shopify.currency.rate
    // belongs to a DIFFERENT (stale) currency and would badly distort the
    // converted amount, so fall back to no conversion (rate 1) instead.
    this.currentCurrency = serverCurrency || clientCurrency.active || 'USD';
    const clientRate = Number(clientCurrency.rate);
    this.conversionRate = (clientCurrencyMatches && clientRate) ? clientRate : 1;

    this.moneyFormat = this.dataset.moneyFormat;
    this.showCurrencyCode = this.dataset.showCurrencyCode === 'true';
    this.empty = this.dataset.empty === 'true';

    this.qualifyingSubtotal = Number(this.dataset.qualifyingSubtotal || 0);
    this.shippingThreshold = Math.round(Number(this.dataset.shippingThreshold || 0) * this.conversionRate);
    this.giftThreshold = Math.round(Number(this.dataset.giftThreshold || 0) * this.conversionRate);
    this.shippingLabel = this.dataset.shippingLabel || 'Free Shipping';
    this.giftLabel = this.dataset.giftLabel || 'Free Gift';
    this.giftAvailable = this.dataset.giftAvailable === 'true';
    this.giftInCart = this.dataset.giftInCart === 'true';
    this.giftLineKey = this.dataset.giftLineKey || '';
    this.giftVariantId = this.dataset.giftVariantId || '';
    this.cartAddUrl = this.dataset.cartAddUrl;
    this.cartChangeUrl = this.dataset.cartChangeUrl;
    this.cartUpdateUrl = this.dataset.cartUpdateUrl;
  }

  connectedCallback() {
    this.renderProgress();
    if (!this.empty) this.syncGift();
    watchExternalCartChanges();
    guardGiftCheckout();
  }

  // The gift is coupled to FREE SHIPPING: a shopper only keeps it when the
  // qualifying subtotal clears BOTH the gift threshold and the shipping
  // threshold. This is the core rule that stops "gift + paid shipping"
  // orders -- under the free-shipping bar, giftQualifies() is false, so the
  // gift is never added and any existing gift line is removed.
  giftQualifies() {
    return (
      this.giftAvailable &&
      this.qualifyingSubtotal >= this.giftThreshold &&
      this.qualifyingSubtotal >= this.shippingThreshold
    );
  }

  getFormattedPrice(price) {
    const formatted = Shopify.formatMoney(Math.max(price, 0), this.moneyFormat);
    return this.showCurrencyCode ? `${formatted} ${this.currentCurrency}` : formatted;
  }

  renderProgress() {
    const fill = this.querySelector('[data-progress-bar-fill]');
    const messageEl = this.querySelector('[data-progress-text]');
    const statusEl = this.querySelector('[data-status-label]');

    const maxThreshold = this.giftAvailable
      ? Math.max(this.shippingThreshold, this.giftThreshold)
      : this.shippingThreshold;

    if (fill && maxThreshold > 0) {
      const pct = Math.min(100, Math.max(0, (this.qualifyingSubtotal / maxThreshold) * 100));
      fill.style.width = `${pct}%`;
    }

    const shippingDone = this.qualifyingSubtotal >= this.shippingThreshold;
    const giftDone = this.giftAvailable && this.qualifyingSubtotal >= this.giftThreshold;

    const milestones = [{ label: this.shippingLabel, threshold: this.shippingThreshold, done: shippingDone }];
    if (this.giftAvailable) {
      milestones.push({ label: this.giftLabel, threshold: this.giftThreshold, done: giftDone });
    }
    milestones.sort((a, b) => a.threshold - b.threshold);

    const doneLabels = milestones.filter(m => m.done).map(m => m.label);
    const pending = milestones.filter(m => !m.done);

    if (statusEl) {
      if (doneLabels.length) {
        statusEl.textContent = `${doneLabels.join(' + ')}!`;
        statusEl.classList.remove('hidden');
      } else {
        statusEl.classList.add('hidden');
      }
    }

    if (messageEl) {
      if (pending.length === 0) {
        messageEl.textContent = `You've unlocked ${doneLabels.join(' + ')} on this order!`;
      } else {
        const nextThreshold = pending[0].threshold;
        const nextLabels = pending.filter(m => m.threshold === nextThreshold).map(m => m.label);
        const remaining = this.getFormattedPrice(nextThreshold - this.qualifyingSubtotal);
        messageEl.textContent = `Add ${remaining} more to get ${nextLabels.join(' + ')} on this order`;
      }
    }
  }

  syncGift() {
    if (!this.giftVariantId) return;

    // This element gets replaced on every cart refresh. If a delayed retry
    // fires on a detached (stale) copy, drop it -- the freshly rendered bar
    // has the up-to-date gift state and will run its own sync.
    if (!this.isConnected) return;

    if (window.__mysteryGiftSyncing) {
      // Another gift add/remove triggered moments ago (e.g. by a different
      // cart update) is still in flight. Don't silently drop this check --
      // retry shortly so the gift still gets added/removed once that
      // in-flight request settles, instead of leaving the cart stuck.
      window.setTimeout(() => this.syncGift(), 400);
      return;
    }

    if (!this.giftAvailable) {
      // Gift is disabled or out of stock. If it's already sitting in the
      // cart from before (e.g. it sold out after being added), take it back
      // out rather than leaving a gift line the store can't actually honor.
      if (this.giftInCart) this.removeGift();
      return;
    }

    // Coupled to free shipping (see giftQualifies): below the shipping bar the
    // gift is not kept, which is what stops the #15853 "gift + paid shipping".
    const shouldHaveGift = this.giftQualifies();

    if (shouldHaveGift && !this.giftInCart) {
      this.addGift();
    } else if (!shouldHaveGift && this.giftInCart) {
      this.removeGift();
    }
  }

  addGift() {
    window.__mysteryGiftSyncing = true;
    const finish = () => { window.__mysteryGiftSyncing = false; };
    const payload = {
      items: [
        {
          id: this.giftVariantId,
          quantity: 1,
          properties: {
            _mystery_gift: 'true',
            _mystery_gift_note: 'Automatically added free gift'
          }
        }
      ]
    };

    // Go through the shared QUEUED engine: this serializes the gift add with
    // any other in-flight cart action (no more "There was an error while
    // updating your cart" conflicts from colliding requests) and re-renders
    // all cart sections in the same round trip.
    if (typeof window.arCartMutate === 'function') {
      window.arCartMutate(this.cartAddUrl, payload).then(finish, finish);
      return;
    }

    fetch(this.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(response => response.json())
      .then(() => this.refreshSections())
      .catch(error => {
        console.error('Mystery gift auto-add failed', error);
        window.__mysteryGiftSyncing = false;
      });
  }

  removeGift() {
    if (!this.giftLineKey) return;
    window.__mysteryGiftSyncing = true;
    const finish = () => { window.__mysteryGiftSyncing = false; };
    const payload = { id: this.giftLineKey, quantity: 0 };

    // Same as addGift: serialized through the shared engine.
    if (typeof window.arCartMutate === 'function') {
      window.arCartMutate(this.cartChangeUrl, payload).then(finish, finish);
      return;
    }

    fetch(this.cartChangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(response => response.json())
      .then(() => this.refreshSections())
      .catch(error => {
        console.error('Mystery gift auto-remove failed', error);
        window.__mysteryGiftSyncing = false;
      });
  }

  refreshSections() {
    const finish = () => { window.__mysteryGiftSyncing = false; };

    // Delegate to the unified cart engine: one request that re-renders the
    // cart page, the drawer and the header counter together.
    if (typeof window.arCartRefreshSections === 'function') {
      window.arCartRefreshSections().then(finish, finish);
      return;
    }

    finish();
  }
}

customElements.define('cart-progress-bar', CartProgressBar);

/*
 * Checkout guard (order #15853 backstop for the async-removal race).
 *
 * The gift is auto-removed whenever the cart drops below the free-shipping
 * bar, but that removal is asynchronous -- a shopper can click Checkout in the
 * split second before it lands. This intercepts the theme's Checkout button
 * (<button name="checkout">): if a gift is still in the cart but the cart no
 * longer qualifies (giftQualifies() === false), it removes the gift FIRST and
 * then continues to checkout.
 *
 * It FAILS OPEN by design: a real sale must never be blocked, so if the
 * removal errors or hangs beyond a short timeout, checkout proceeds anyway.
 * Express / dynamic-checkout buttons (Shop Pay, PayPal, Buy Now) submit
 * through their own flow and are NOT covered here -- the server-side order
 * flag is the backstop for those rare cases.
 */
function guardGiftCheckout() {
  if (window.__giftCheckoutGuardAttached) return;
  window.__giftCheckoutGuardAttached = true;

  document.addEventListener(
    'click',
    function (event) {
      const trigger =
        event.target && event.target.closest ? event.target.closest('[name="checkout"]') : null;
      if (!trigger) return;

      const bar = document.querySelector('cart-progress-bar');
      if (!bar || typeof bar.giftQualifies !== 'function') return;

      // Only intervene when a gift is in the cart but the cart no longer earns
      // it. Everything else checks out completely untouched.
      if (!bar.giftInCart || bar.giftQualifies()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      let proceeded = false;
      const proceed = function () {
        if (proceeded) return;
        proceeded = true;
        window.location.href = '/checkout';
      };

      // Fail OPEN: never hold a sale hostage to gift removal.
      const failOpen = window.setTimeout(proceed, 2000);
      const done = function () {
        window.clearTimeout(failOpen);
        proceed();
      };

      try {
        if (bar.giftLineKey && typeof window.arCartMutate === 'function' && bar.cartChangeUrl) {
          window
            .arCartMutate(bar.cartChangeUrl, { id: bar.giftLineKey, quantity: 0 })
            .then(done, done);
        } else if (typeof bar.removeGift === 'function') {
          bar.removeGift();
          window.setTimeout(done, 800);
        } else {
          proceed();
        }
      } catch (e) {
        console.error('Gift checkout guard failed; proceeding to checkout', e);
        proceed();
      }
    },
    true
  );
}

/*
 * Watches the cart's rendered total-price text (both the cart-page and
 * cart-drawer copies, whichever exist) for changes coming from OUTSIDE the
 * theme's own cart engine -- e.g. a discount-code app block, or Shopify's
 * own cart-level discount code UI, both of which can update the total
 * without going through window.arCartMutate. When such a change is detected,
 * it asks the live <cart-progress-bar> to refresh, which re-pulls fresh
 * server-rendered Liquid data -- so if a discount code drops the cart below
 * a threshold, the free shipping/gift bar and the auto-added gift line
 * correctly reset, exactly like removing a cart item already does.
 *
 * Changes made by our own engine are ignored (window.__arCartQuietUntil is
 * set while it updates the DOM) -- the engine already applied fresh
 * server-rendered sections, so a follow-up refresh would just be a redundant
 * network request on every single cart action (slow, especially on mobile).
 *
 * Prefers the grand-total value (cart-summary__grand-total-value, only
 * rendered once a discount is applied and reflects the real post-discount
 * payable total) over the plain subtotal (cart-summary__total-value).
 */
function watchExternalCartChanges() {
  if (window.__cartTotalWatcherAttached) return;
  window.__cartTotalWatcherAttached = true;

  const roots = [document.getElementById('main-cart'), document.getElementById('CartDrawer')].filter(Boolean);
  if (!roots.length) return;

  const lastTotals = new WeakMap();
  let debounceTimer = null;

  const scheduleResync = () => {
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      debounceTimer = null;
      // Our own engine caused this change and is already in sync -- skip.
      if (window.__arCartQuietUntil && Date.now() < window.__arCartQuietUntil) return;
      const bar = document.querySelector('cart-progress-bar');
      if (bar && typeof bar.refreshSections === 'function' && !window.__mysteryGiftSyncing) {
        bar.refreshSections();
      }
    }, 500);
  };

  roots.forEach(root => {
    const observer = new MutationObserver(() => {
      const grandTotalEl = root.querySelector('.cart-summary__grand-total-value');
      const totalEl = grandTotalEl || root.querySelector('.cart-summary__total-value');
      if (!totalEl) return;
      const text = totalEl.textContent.trim();
      const previous = lastTotals.get(root);
      if (previous === undefined) {
        lastTotals.set(root, text);
        return;
      }
      if (text !== previous) {
        lastTotals.set(root, text);
        scheduleResync();
      }
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  });
}